import {
    Autocomplete,
    AutocompleteRenderInputParams,
    TextField,
    Chip,
} from "@mui/material"
import { useCallback, useMemo } from "react"

import {
    IGenericAutoCompleteTextFieldValue,
    IAutoCompleteTextFieldProps,
} from "src/types/auto-complete-text-field"
import { sortAlphabeticallyByLabel } from "src/lib/array"

// Autocomplete text field dropdown

export const AutoCompleteTextField = <
    T extends IGenericAutoCompleteTextFieldValue,
>(
    props: IAutoCompleteTextFieldProps<T>,
) => {
    // Sort options alphabetically
    const options = useMemo(() => {
        if (props.alphabeticallySorted != null) {
            return sortAlphabeticallyByLabel(props.options)
        }
        return props.options
    }, [props.options, props.alphabeticallySorted])

    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField
                {...params}
                variant="outlined"
                label={props.label}
                placeholder={props.placeholder}
                error={props.error ?? false}
                helperText={props.helperText ?? ""}
                data-testid={"autocomplete-text-field-input"}
                sx={{
                    backgroundColor: "common.white",
                    ...props.sx,
                }}
            />
        ),
        [
            props.label,
            props.placeholder,
            props.error,
            props.helperText,
            props.sx,
        ],
    )

    const isOptionEqualToValue = useCallback(
        (option: T, value: T) => option.id === value.id,
        [],
    )

    // Default renderTags implementation for multiple selection
    const defaultRenderTags = useCallback((value: T[], getTagProps) => {
        return value?.map((option, index) => (
            <Chip
                key={option.id}
                label={option.label}
                title={option.label} // Show full name on hover
                {...getTagProps({ index })}
            />
        ))
    }, [])

    // Add renderOption to provide unique keys to list items
    const renderOption = useCallback(
        (props: React.HTMLAttributes<HTMLLIElement>, option: T) => {
            return (
                <li {...props} key={option.id}>
                    {option.label}
                </li>
            )
        },
        [],
    )

    return (
        <Autocomplete
            {...props}
            options={options ?? []}
            multiple={props.multiple ?? false}
            isOptionEqualToValue={isOptionEqualToValue}
            renderInput={renderInput}
            renderTags={props.renderTags ?? defaultRenderTags}
            renderOption={props.renderOption ?? renderOption}
            data-testid={"autocomplete-text-field"}
        />
    )
}
